import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
// Environment detection (inline to avoid circular dependency with env-helpers)
function getEnvironment() {
    if (typeof window === 'undefined')
        return 'production';
    const hostname = window.location.hostname;
    // Check for staging prefix in Pages URL or custom domains
    // Matches: staging.*, *-staging.*, staging-* (weretrade pattern)
    if (hostname.startsWith('staging.') || hostname.startsWith('staging-') || hostname.includes('-staging.')) {
        return 'staging';
    }
    // Check for dev prefix
    if (hostname.startsWith('dev.') || hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'dev';
    }
    // Non-production Pages URLs (*.pages.dev without custom domain)
    if (hostname.endsWith('.pages.dev') && !hostname.includes('.weretrade.com')) {
        return 'staging';
    }
    return 'production';
}
const DISMISS_KEY = 'staging-banner-dismissed';
export function StagingBanner({ forceShow = false, message, dismissable = true, }) {
    const [dismissed, setDismissed] = useState(false);
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        setMounted(true);
        // Check sessionStorage for dismiss preference
        if (dismissable && typeof window !== 'undefined') {
            const wasDismissed = sessionStorage.getItem(DISMISS_KEY) === 'true';
            setDismissed(wasDismissed);
        }
    }, [dismissable]);
    // Don't render during SSR
    if (!mounted)
        return null;
    const env = getEnvironment();
    // Only show in non-production environments (unless forced)
    if (!forceShow && env === 'production') {
        return null;
    }
    // Don't show if dismissed
    if (dismissed) {
        return null;
    }
    const handleDismiss = () => {
        setDismissed(true);
        if (typeof window !== 'undefined') {
            sessionStorage.setItem(DISMISS_KEY, 'true');
        }
    };
    const envLabel = env === 'staging' ? 'STAGING' : env === 'dev' ? 'DEVELOPMENT' : 'TEST';
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const displayMessage = message || `You are viewing the ${envLabel} environment`;
    return (_jsxs("div", { style: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            backgroundColor: env === 'staging' ? '#f59e0b' : '#3b82f6',
            color: '#000',
            padding: '0.5rem 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }, role: "alert", "aria-live": "polite", children: [_jsx("span", { style: {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    padding: '0.125rem 0.5rem',
                    borderRadius: '4px',
                    fontWeight: 700,
                }, children: envLabel }), _jsx("span", { children: displayMessage }), _jsxs("span", { style: {
                    opacity: 0.7,
                    fontSize: '0.75rem',
                }, children: ["(", hostname, ")"] }), dismissable && (_jsx("button", { onClick: handleDismiss, style: {
                    marginLeft: 'auto',
                    background: 'rgba(0,0,0,0.2)',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '0.25rem 0.5rem',
                    cursor: 'pointer',
                    color: 'inherit',
                    fontSize: '0.75rem',
                }, "aria-label": "Dismiss staging banner", children: "Dismiss" }))] }));
}
/**
 * Spacer component to add padding when StagingBanner is shown.
 * Use this in your layout to prevent content from being hidden under the banner.
 *
 * Usage:
 * ```tsx
 * <StagingBanner />
 * <StagingBannerSpacer />
 * <AppContent />
 * ```
 */
export function StagingBannerSpacer() {
    const [shouldShow, setShouldShow] = useState(false);
    useEffect(() => {
        const env = getEnvironment();
        const dismissed = sessionStorage.getItem(DISMISS_KEY) === 'true';
        setShouldShow(env !== 'production' && !dismissed);
    }, []);
    if (!shouldShow)
        return null;
    return _jsx("div", { style: { height: '2.5rem' }, "aria-hidden": "true" });
}
